import React, { useCallback, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import clsx from 'classnames'
import { Link, useLocation } from 'react-router'
import { useSelector } from 'react-redux'
import type { NewsList as NewsListData, PromotionsList } from 'api/api.types'
import { APIService } from 'api/APIService'
import { getCachedNews, getCachedPromotions } from 'src/store/data.store'
import { getSelectedCityId } from 'src/store/app.state.store'
import type { RootState } from 'src/store'
import { usePagination } from 'src/hooks/usePagination'
import { parseHtml } from 'utils/htmlParserWithCustomLinks'
import { Loader } from 'components/Utility/Loader/Loader'
import { Picture } from 'components/Utility/Media/Picture'
import { PageNotFound } from 'components/PageElements/PageNotFound/PageNotFound'
import styles from './NewsList.module.scss'

type ListData = NewsListData | PromotionsList

/** Query parameter with the zero-based list page (the server preloads that page) */
const LIST_PARAM = 'list'

type NewsListProps = {
  perPage?: number,
  noPagination?: boolean,
  /** Lists promotions instead of news */
  promotions?: boolean,
}

/** News or promotions of the selected city with pages */
export const NewsList: React.FC<NewsListProps> = (props) => {
  const { perPage = 6, noPagination = false, promotions } = props
  const currentCityId = useSelector(getSelectedCityId)
  const cachedNews = useSelector<RootState, ListData | null>(promotions ? getCachedPromotions : getCachedNews)
  const location = useLocation()
  const [news, setNews] = useState<ListData>(() => cachedNews ?? { pages: [], total: 0 })
  const [newsLoading, setNewsLoading] = useState(false)

  const page = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)
    return Number(searchParams.get(LIST_PARAM) ?? '0')
  }, [location])

  useEffect(() => {
    setNewsLoading(true)
    const request = promotions
      ? APIService.data.fetchPromotions(currentCityId, page, perPage)
      : APIService.data.fetchNews(currentCityId, page, perPage)
    request
      .then((result: ListData) => setNews(result))
      .finally(() => setNewsLoading(false))
  }, [page, currentCityId, perPage, promotions])

  const renderPage = useCallback((pageIndex: number) => (
    <div key={pageIndex} className={styles.page}>
      <Link
        className={clsx([styles.pageLink, { [styles.active]: pageIndex === page }])}
        to={`?${LIST_PARAM}=${pageIndex}`}
      >
        {pageIndex + 1}
      </Link>
    </div>
  ), [page])

  const pages = usePagination(renderPage, news.total, perPage, page)

  if (page !== 0 && news.pages.length === 0 && !newsLoading) {
    return <PageNotFound />
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {newsLoading && <Loader />}
        {news.pages.map((item, index) => {
          const vars = item.templateVars?.pageVars
          const date = moment(item.publishedAt)
          const image = vars?.image?.type === 'image' ? vars.image : null
          return <Link
            to={item.fullPath}
            className={clsx({ [styles.first]: page === 0 && index === 0, [styles.newsBox]: true })}
            key={index}
          >
            <article className={styles.newsContent} itemScope itemType="http://schema.org/NewsArticle">
              <div className={styles.imgBox}>
                <Picture
                  className={styles.img}
                  image={image?.image}
                  desktopType={'list'}
                  mobileType={'list'}
                  alt={image?.imageAlt}
                />
              </div>
              <header className={styles.header}>
                <time itemProp="datePublished" dateTime={date.format('YYYY-MM-DD')} className={styles.date}>
                  {date.format('DD.MM.YYYY')}
                </time>
                <h2 itemProp="name" className={styles.name}>
                  {item.title}
                </h2>
              </header>
              <div itemProp="description" className={clsx([styles.description, 'text-content'])}>
                {parseHtml(vars?.shortText ?? '')}
              </div>
              <div className={styles.link}>Подробнее</div>
            </article>
          </Link>
        })}
      </div>
      {!noPagination && (
        <div className={styles.pages}>
          {pages}
        </div>
      )}
    </div>
  )
}
